import { QueryClient, useMutation } from "@tanstack/react-query"
import { MainLoggedInLayout } from "../../components/layouts/main.loggedIn.layout"
import { createCommunity } from "../../controllers/community/community"
import { useContext, useEffect, useState } from "react"
import { UserContext } from "../../context/UserContext"
import { 
    Button, 
    Card, 
    CardActions, 
    CardContent, 
    CardHeader, 
    FormControl, 
    FormControlLabel, 
    FormLabel, 
    Grid, 
    TextField,
    Typography,
    Box,
    Divider,
    Paper,
    Chip,
    Stack,
    Alert,
    IconButton,
    Radio,
    RadioGroup,
    Container,
    Switch
} from "@mui/material"
import { 
    Groups as GroupsIcon,
    Description as DescriptionIcon,
    Category as CategoryIcon,
    Image as ImageIcon,
    PhotoCamera as PhotoCameraIcon,
    Collections as CollectionsIcon,
    Lock as LockIcon,
    Public as PublicIcon,
    Security as SecurityIcon
} from "@mui/icons-material"
import { PostMedia } from "../../components/post/card/PostMedia"
import { useNavigate } from "react-router"
import { CategoryPicker } from "../../components/category.picker"
import { header_radio_set } from "../../util/util"

export const CreateCommunity = () => {
    const { userDetails } = useContext(UserContext)
    const [header_img, setHeaderImg] = useState('')
    const [media_ids, setMediaIds] = useState([])
    const [imageUrls, setImageUrls] = useState([])
    const [chooseHeader, setChooseHeader] = useState(true)
    const [category, setCategory] = useState('')
    const [privacy, setPrivacy] = useState(false)
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [errors, setErrors] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const navigate = useNavigate()

    const validateForm = () => {
        const newErrors = {}
        
        if (!title.trim()) newErrors.title = 'Community title is required'
        if (title.trim().length < 3) newErrors.title = 'Title must be at least 3 characters long'
        if (!description.trim()) newErrors.description = 'Community description is required'
        if (description.trim().length < 10) newErrors.description = 'Description must be at least 10 characters long'
        if (!category) newErrors.category = 'Community category is required'
        
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const queryClient = new QueryClient()
    const mutation = useMutation({
        mutationFn: createCommunity,
        onSuccess: (data) => {
            setIsSubmitting(false)
            queryClient.invalidateQueries({ queryKey: ['createCommunity'] })
            navigate("/community/show/" + data.community._id)
        },
        onError: (error) => {
            setIsSubmitting(false)
            console.error('Error creating community:', error)
        }
    })

    useEffect(() => {
        if (imageUrls.length > 0) {
            setHeaderImg(imageUrls[imageUrls.length - 1])
        }
    }, [imageUrls])

    useEffect(() => {
        setHeaderImg('https://storage.googleapis.com/events.makegayfriends.com/event_images/uploads/community-1.jpg')
    }, [])
    
    const sendCreateCommunity = () => {
        if (!validateForm()) return
        
        setIsSubmitting(true)
        mutation.mutate({
            title,
            description,
            header_img,
            leader: [userDetails?.id],
            token: userDetails?.token,
            privacy,
            category
        })
    }

    return (
        <MainLoggedInLayout>
            <Container maxWidth="md" sx={{ py: 3 }}>
                <Card elevation={3} sx={{ borderRadius: 2 }}>
                    <CardHeader
                        avatar={<GroupsIcon color="primary" />}
                        title={
                            <Typography variant="h4" component="h1" fontWeight="bold">
                                Create New Community
                            </Typography>
                        }
                        subheader={
                            <Typography variant="body2" color="text.secondary">
                                {new Date().toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                })}
                            </Typography>
                        }
                        sx={{ pb: 1 }}
                    />
                    
                    <CardContent sx={{ px: 3 }}>
                        <Stack spacing={4}>
                            {/* Basic Community Information */}
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DescriptionIcon color="primary" />
                                    Community Details
                                </Typography>
                                
                                <Stack spacing={3}>
                                    <TextField
                                        fullWidth
                                        label="Community Name"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        error={!!errors.title}
                                        helperText={errors.title || "Choose a unique and memorable name for your community"}
                                        variant="outlined"
                                        placeholder="Enter your community name"
                                    />
                                    
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Community Description"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        error={!!errors.description}
                                        helperText={errors.description || "Describe what your community is about and what members can expect"}
                                        variant="outlined"
                                        placeholder="Tell people what your community is about, its purpose, and what members can expect..."
                                    />
                                </Stack>
                            </Paper>

                            {/* Privacy Settings */}
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SecurityIcon color="primary" />
                                    Privacy Settings
                                </Typography>
                                
                                <Box sx={{ mt: 2 }}>
                                    <FormControl component="fieldset" sx={{ width: '100%' }}>
                                        <RadioGroup
                                            value={privacy}
                                            onChange={(e) => setPrivacy(e.target.value === 'true')}
                                            sx={{ gap: 2 }}
                                        >
                                            <Paper 
                                                elevation={privacy === false ? 2 : 0} 
                                                sx={{ 
                                                    p: 2, 
                                                    border: privacy === false ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                                    borderRadius: 2,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    '&:hover': { boxShadow: 2 }
                                                }}
                                                onClick={() => setPrivacy(false)}
                                            >
                                                <FormControlLabel
                                                    value={false}
                                                    control={<Radio />}
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <PublicIcon color={privacy === false ? 'primary' : 'disabled'} />
                                                            <Box>
                                                                <Typography variant="subtitle1" fontWeight="bold">
                                                                    Public Community
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Anyone can find and join your community
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    }
                                                    sx={{ margin: 0, width: '100%' }}
                                                />
                                            </Paper>
                                            
                                            <Paper 
                                                elevation={privacy === true ? 2 : 0} 
                                                sx={{ 
                                                    p: 2, 
                                                    border: privacy === true ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                                    borderRadius: 2,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    '&:hover': { boxShadow: 2 }
                                                }}
                                                onClick={() => setPrivacy(true)}
                                            >
                                                <FormControlLabel
                                                    value={true}
                                                    control={<Radio />}
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <LockIcon color={privacy === true ? 'primary' : 'disabled'} />
                                                            <Box>
                                                                <Typography variant="subtitle1" fontWeight="bold">
                                                                    Private Community
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Members need approval to join
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    }
                                                    sx={{ margin: 0, width: '100%' }}
                                                />
                                            </Paper>
                                        </RadioGroup>
                                    </FormControl>
                                </Box>
                            </Paper>

                            {/* Category */}
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CategoryIcon color="primary" />
                                    Category
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Choose a category that best describes your community
                                </Typography>
                                
                                <Box sx={{ mt: 2 }}>
                                    <CategoryPicker 
                                        category={category} 
                                        setCategory={setCategory} 
                                    />
                                    {errors.category && (
                                        <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5, display: 'block' }}>
                                            {errors.category}
                                        </Typography>
                                    )}
                                </Box>
                            </Paper>

                            {/* Community Image */}
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ImageIcon color="primary" />
                                    Community Image
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Choose an image that represents your community
                                </Typography>
                                
                                <Stack spacing={2}>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Button
                                            variant={chooseHeader ? "contained" : "outlined"}
                                            startIcon={<CollectionsIcon />}
                                            onClick={() => setChooseHeader(true)}
                                            size="small"
                                        >
                                            Choose from Gallery
                                        </Button>
                                        <Button
                                            variant={!chooseHeader ? "contained" : "outlined"}
                                            startIcon={<PhotoCameraIcon />}
                                            onClick={() => setChooseHeader(false)}
                                            size="small"
                                        >
                                            Upload Custom Image
                                        </Button>
                                    </Box>

                                    {chooseHeader ? (
                                        <Box>
                                            <FormControl component="fieldset" sx={{ width: '100%' }}>
                                                <RadioGroup
                                                    value={header_img}
                                                    onChange={(e) => setHeaderImg(e.target.value)}
                                                    sx={{ mt: 1 }}
                                                >
                                                    <Grid container spacing={2}>
                                                        {header_radio_set.map((radio, index) => (
                                                            <Grid item xs={6} sm={4} md={3} key={index}>
                                                                <FormControlLabel
                                                                    value={`https://storage.googleapis.com/events.makegayfriends.com/event_images/uploads/${radio}`}
                                                                    control={<Radio />}
                                                                    label={
                                                                        <Box
                                                                            component="img"
                                                                            src={`https://storage.googleapis.com/events.makegayfriends.com/event_images/uploads/${radio}`}
                                                                            alt="Community option"
                                                                            sx={{
                                                                                width: '100%',
                                                                                height: 80,
                                                                                objectFit: 'cover',
                                                                                borderRadius: 1,
                                                                                border: header_img === `https://storage.googleapis.com/events.makegayfriends.com/event_images/uploads/${radio}` 
                                                                                    ? '3px solid #1976d2' : '1px solid #ddd',
                                                                                cursor: 'pointer',
                                                                                transition: 'all 0.2s',
                                                                                '&:hover': {
                                                                                    transform: 'scale(1.02)',
                                                                                    boxShadow: 2
                                                                                }
                                                                            }}
                                                                        />
                                                                    }
                                                                    sx={{ margin: 0, width: '100%' }}
                                                                />
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </RadioGroup>
                                            </FormControl>
                                        </Box>
                                    ) : (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                                Upload Custom Community Image
                                            </Typography>
                                            <PostMedia 
                                                imageUrls={imageUrls} 
                                                setImageUrls={setImageUrls} 
                                                media_ids={media_ids} 
                                                setMediaIds={setMediaIds} 
                                                token={userDetails?.token} 
                                            />
                                        </Box>
                                    )}
                                </Stack>
                            </Paper>

                            {/* Error Alert */}
                            {Object.keys(errors).length > 0 && (
                                <Alert severity="error" sx={{ borderRadius: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Please fix the following errors:
                                    </Typography>
                                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                        {Object.values(errors).map((error, index) => (
                                            <li key={index}>
                                                <Typography variant="body2">{error}</Typography>
                                            </li>
                                        ))}
                                    </ul>
                                </Alert>
                            )}

                            {/* Success Preview */}
                            {title && description && category && (
                                <Paper elevation={1} sx={{ p: 3, borderRadius: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                                    <Typography variant="h6" gutterBottom>
                                        🎉 Community Preview
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Your community "{title}" is ready to be created! 
                                        {privacy ? ' It will be private and require approval for new members.' : ' It will be public and anyone can join.'}
                                    </Typography>
                                </Paper>
                            )}
                        </Stack>
                    </CardContent>
                    
                    <CardActions sx={{ p: 3, pt: 0 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={sendCreateCommunity}
                            disabled={isSubmitting}
                            sx={{
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                borderRadius: 2,
                                textTransform: 'none',
                                boxShadow: 3,
                                '&:hover': {
                                    boxShadow: 6,
                                    transform: 'translateY(-1px)'
                                }
                            }}
                        >
                            {isSubmitting ? 'Creating Community...' : '🚀 Create Community'}
                        </Button>
                    </CardActions>
                </Card>
            </Container>
        </MainLoggedInLayout>
    )
}